import {
  Bell,
  Building2,
  Building,
  Cable,
  Gauge,
  HardDrive,
  History,
  LayoutGrid,
  MonitorPlay,
  Tv,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** One-line purpose, shown in the command palette and page headers. */
  summary: string;
  /** Extra search terms for the command palette. */
  keywords: string[];
  /** Hidden from navigation unless the user holds this permission. */
  permission?: string;
  /**
   * Operations staff only: hidden from users who only watch their cameras
   * (a monitoring company's clients, ADR-019).
   */
  staff?: boolean;
  /**
   * The platform tenant (ADR-028) has no cameras: 'only' items exist there
   * alone, 'never' items (the operation) are hidden there.
   */
  platform?: 'only' | 'never';
}

/** Any of these makes a user part of the operation, not a watch-only client. */
const staffPermissions = [
  'events.ack',
  'devices.manage',
  'sites.manage',
  'clients.manage',
  'users.read',
];

/**
 * The navigation items a user may see. People of a client (ADR-027) never
 * see the operation's screens, whatever their profile holds.
 */
export function visibleItems(
  items: NavItem[],
  can: (p: string) => boolean,
  clientPerson = false,
  platform = false,
): NavItem[] {
  const staff = !clientPerson && staffPermissions.some(can);
  return items.filter(
    (i) =>
      (!i.permission || can(i.permission)) &&
      (!i.staff || staff) &&
      (platform ? i.platform !== 'never' : i.platform !== 'only'),
  );
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    label: 'Plataforma',
    items: [
      {
        to: '/overview',
        label: 'Painel',
        icon: Gauge,
        summary: 'Visão geral: sistema, empresas, clientes, câmeras, gravadores e integrações',
        keywords: [
          'painel',
          'dashboard',
          'visao geral',
          'resumo',
          'totais',
          'plataforma',
          'sistema',
          'status',
          'saude',
          'health',
          'dependencias',
          'servicos',
          'quem esta assistindo',
        ],
        permission: 'platform.tenants',
        platform: 'only',
      },
      {
        to: '/tenants',
        label: 'Empresas',
        icon: Building,
        summary: 'Empresas da plataforma: criar, suspender e entrar',
        keywords: ['empresa', 'organizacao', 'plataforma', 'super', 'tenant'],
        permission: 'platform.tenants',
        platform: 'only',
      },
      {
        to: '/storage',
        label: 'Armazenamento',
        icon: HardDrive,
        summary: 'Bucket S3 (Backblaze B2) onde ficam as gravações',
        keywords: [
          'armazenamento',
          's3',
          'backblaze',
          'b2',
          'bucket',
          'nuvem',
          'gravacoes',
          'disco',
        ],
        permission: 'platform.storage',
        platform: 'only',
      },
      {
        to: '/media-nodes',
        label: 'Máquinas de vídeo',
        icon: Server,
        summary: 'Servidores de vídeo: cadastro, saúde, ocupação e distribuição dos equipamentos',
        keywords: [
          'maquina',
          'maquinas',
          'video',
          'servidor',
          'zlmediakit',
          'capacidade',
          'ocupacao',
        ],
        permission: 'platform.media_nodes',
        platform: 'only',
      },
    ],
  },
  {
    label: 'Operação',
    items: [
      {
        to: '/live',
        label: 'Ao vivo',
        icon: LayoutGrid,
        summary: 'Monitoramento em tempo real e mosaicos de câmeras',
        keywords: ['monitoramento', 'mosaico', 'live', 'cameras'],
        permission: 'live.view',
        platform: 'never',
      },
      {
        to: '/monitoring',
        label: 'Monitoramento',
        icon: Tv,
        summary: 'Painel de monitoramento em tela cheia: mosaicos compartilhados e matriz virtual',
        keywords: [
          'monitoramento',
          'operador',
          'mosaico',
          'matriz virtual',
          'situator',
          'tela cheia',
        ],
        permission: 'monitoring.view',
        platform: 'never',
      },
      {
        to: '/mosaics',
        label: 'Mosaicos',
        icon: MonitorPlay,
        summary: 'Mosaicos salvos, cada um aberto numa janela só com as câmeras',
        keywords: ['mosaico', 'janela', 'parede', 'monitor', 'videowall', 'layout'],
        permission: 'live.view',
        platform: 'never',
      },
      {
        to: '/playback',
        label: 'Reprodução',
        icon: History,
        summary: 'Gravações, linha do tempo e exportação de evidências',
        keywords: ['gravacao', 'playback', 'timeline', 'exportar'],
        permission: 'recordings.view',
        platform: 'never',
      },
      {
        to: '/evidence',
        label: 'Evidências',
        icon: ShieldCheck,
        summary: 'Trechos de gravação protegidos da retenção',
        keywords: ['evidencia', 'ocorrencia', 'proteger', 'boletim', 'custodia'],
        permission: 'recordings.view',
        platform: 'never',
      },
      {
        to: '/events',
        label: 'Registros',
        icon: Bell,
        summary: 'Registro do que acontece nos equipamentos',
        keywords: ['eventos', 'alarme', 'movimento', 'ocorrencia', 'offline', 'registro'],
        permission: 'events.view',
        platform: 'never',
      },
    ],
  },
  {
    label: 'Infraestrutura',
    items: [
      {
        to: '/devices',
        label: 'Dispositivos',
        icon: Video,
        summary: 'Câmeras, DVRs e NVRs cadastrados',
        keywords: ['camera', 'dvr', 'nvr', 'onvif', 'rtsp'],
        permission: 'devices.read',
        staff: true,
        platform: 'never',
      },
      {
        to: '/clients',
        label: 'Clientes',
        icon: Building2,
        summary: 'Clientes e as contas (locais monitorados) de cada um',
        keywords: ['cliente', 'conta', 'local', 'site', 'unidade', 'cpf', 'cnpj'],
        permission: 'clients.read',
        staff: true,
        platform: 'never',
      },
    ],
  },
  {
    label: 'Administração',
    items: [
      {
        to: '/users',
        label: 'Usuários',
        icon: Users,
        summary: 'Usuários e o perfil de cada um',
        keywords: ['rbac', 'papel', 'perfil', 'acesso'],
        permission: 'users.read',
      },
      {
        to: '/roles',
        label: 'Perfis e permissões',
        icon: ShieldCheck,
        summary: 'O que cada perfil pode fazer: tabela de permissões',
        keywords: ['perfil', 'permissao', 'rbac', 'papel', 'acesso', 'tabela'],
        permission: 'roles.read',
        staff: true,
      },
      {
        to: '/integrations',
        label: 'Integrações',
        icon: Cable,
        summary: 'Sistemas que recebem vídeo do Pro Guard (RTSP)',
        keywords: ['somaseg', 'rtsp', 'integracao', 'api', 'chave'],
        permission: 'integrations.manage',
        platform: 'never',
      },
      {
        to: '/audit',
        label: 'Auditoria',
        icon: ScrollText,
        summary: 'Registro de acessos e alterações administrativas',
        keywords: ['log', 'historico', 'seguranca', 'eventos de acesso'],
        permission: 'audit.read',
      },
      {
        to: '/settings',
        label: 'Organização',
        icon: Settings,
        summary: 'Nome e fuso horário da organização',
        keywords: ['configuracoes', 'fuso', 'timezone', 'empresa'],
        permission: 'tenant.manage',
      },
    ],
  },
];

export const navItems: NavItem[] = navSections.flatMap((s) => s.items);
